import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Plus, X, Calendar, MapPin, ExternalLink, Trash2, Tag, ShoppingBag } from "lucide-react";
import { toast } from "sonner";

export default function Events() {
  const { user } = useAuth();
  const [tab, setTab] = useState("events"); // events | shop
  const [events, setEvents] = useState([]);
  const [products, setProducts] = useState([]);
  const [openEvent, setOpenEvent] = useState(false);
  const [openProduct, setOpenProduct] = useState(false);

  const load = async () => {
    const [e, p] = await Promise.all([api.get("/events"), api.get("/products")]);
    setEvents(e.data); setProducts(p.data);
  };
  useEffect(() => { load(); }, []);

  const removeEvent = async (id) => {
    if (!window.confirm("Delete this event?")) return;
    try { await api.delete(`/events/${id}`); load(); toast.success("Deleted"); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const removeProduct = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    try { await api.delete(`/products/${id}`); load(); toast.success("Deleted"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="p-6 sm:p-10 max-w-7xl mx-auto">
      <div className="text-[10px] uppercase tracking-[0.35em] text-[#ff4500] font-mono">── Evènman · Boutik</div>
      <h1 className="font-display text-4xl sm:text-5xl mt-3 tracking-tighter">Events & Shop</h1>
      <p className="text-[#a3a3a3] mt-3 text-sm max-w-xl">
        Post Kanaval parties, restaurant openings, concerts. Or list Haitian art, food, services — with a WhatsApp / IG contact button.
      </p>

      <div className="mt-8 flex gap-2">
        <button onClick={() => setTab("events")} data-testid="tab-events" className={`px-5 py-2.5 text-xs uppercase tracking-[0.2em] border ${tab === "events" ? "bg-[#1a1a1a] border-[#ff4500]" : "border-[#2a2a2a]"}`}>
          <Calendar className="w-3.5 h-3.5 inline-block mr-2" /> Events ({events.length})
        </button>
        <button onClick={() => setTab("shop")} data-testid="tab-shop" className={`px-5 py-2.5 text-xs uppercase tracking-[0.2em] border ${tab === "shop" ? "bg-[#1a1a1a] border-[#ff4500]" : "border-[#2a2a2a]"}`}>
          <ShoppingBag className="w-3.5 h-3.5 inline-block mr-2" /> Shop ({products.length})
        </button>
        <div className="flex-1" />
        {tab === "events" ? (
          <button onClick={() => setOpenEvent(true)} data-testid="new-event-btn" className="px-5 py-2.5 bg-[#ff4500] hover:bg-[#ff5722] text-white text-xs uppercase tracking-[0.2em] inline-flex items-center gap-2">
            <Plus className="w-4 h-4" /> New event
          </button>
        ) : (
          <button onClick={() => setOpenProduct(true)} data-testid="new-product-btn" className="px-5 py-2.5 bg-[#ff4500] hover:bg-[#ff5722] text-white text-xs uppercase tracking-[0.2em] inline-flex items-center gap-2">
            <Plus className="w-4 h-4" /> List product
          </button>
        )}
      </div>

      {tab === "events" ? (
        events.length === 0 ? <EmptyState text="No events yet." /> : (
          <div className="mt-6 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {events.map((e) => (
              <div key={e.id} className="bg-[#141414] border border-[#2a2a2a] hover:border-[#ff4500]/50 transition-colors" data-testid={`event-${e.id}`}>
                {e.cover_url && <div className="aspect-video bg-black overflow-hidden"><img src={e.cover_url} alt={e.name} className="w-full h-full object-cover" /></div>}
                <div className="p-4">
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="font-display text-lg tracking-tight">{e.name}</h3>
                    {e.owner_id === user?.id && (
                      <button onClick={() => removeEvent(e.id)} className="text-[#a3a3a3] hover:text-[#ff0033]"><Trash2 className="w-3.5 h-3.5" /></button>
                    )}
                  </div>
                  {e.description && <p className="text-xs text-[#a3a3a3] mt-2 line-clamp-3">{e.description}</p>}
                  <div className="mt-3 space-y-1.5 text-xs text-[#a3a3a3]">
                    {e.event_date && <div className="flex items-center gap-2"><Calendar className="w-3 h-3 text-[#ff4500]" />{e.event_date}</div>}
                    {e.location && <div className="flex items-center gap-2"><MapPin className="w-3 h-3 text-[#ff4500]" />{e.location}</div>}
                    <div className="flex items-center gap-2"><Tag className="w-3 h-3 text-[#ff4500]" />{e.category} · by {e.owner_name}</div>
                  </div>
                  {e.link && (
                    <a href={e.link} target="_blank" rel="noopener noreferrer" className="mt-3 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#ff4500] hover:underline">
                      More info <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        )
      ) : (
        products.length === 0 ? <EmptyState text="No products yet." /> : (
          <div className="mt-6 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {products.map((p) => (
              <div key={p.id} className="bg-[#141414] border border-[#2a2a2a] hover:border-[#ff4500]/50 transition-colors" data-testid={`product-${p.id}`}>
                {p.image_url && <div className="aspect-video bg-black overflow-hidden"><img src={p.image_url} alt={p.name} className="w-full h-full object-cover" /></div>}
                <div className="p-4">
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="font-display text-lg tracking-tight">{p.name}</h3>
                    {p.owner_id === user?.id && (
                      <button onClick={() => removeProduct(p.id)} className="text-[#a3a3a3] hover:text-[#ff0033]"><Trash2 className="w-3.5 h-3.5" /></button>
                    )}
                  </div>
                  {p.description && <p className="text-xs text-[#a3a3a3] mt-2 line-clamp-3">{p.description}</p>}
                  {p.price_display && <div className="mt-3 font-display text-lg text-[#ff4500]">{p.price_display}</div>}
                  <div className="text-xs text-[#a3a3a3] mt-1">by {p.owner_name}</div>
                  {p.contact_link && (
                    <a href={p.contact_link} target="_blank" rel="noopener noreferrer" className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-[#ff4500] hover:bg-[#ff5722] text-white text-xs uppercase tracking-[0.2em]">
                      Contact <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        )
      )}

      {openEvent && <EventDialog onClose={() => setOpenEvent(false)} onDone={() => { setOpenEvent(false); load(); }} />}
      {openProduct && <ProductDialog onClose={() => setOpenProduct(false)} onDone={() => { setOpenProduct(false); load(); }} />}
    </div>
  );
}

function EmptyState({ text }) {
  return <div className="mt-6 border border-dashed border-[#2a2a2a] p-16 text-center text-sm text-[#a3a3a3]">{text}</div>;
}

function EventDialog({ onClose, onDone }) {
  const [form, setForm] = useState({ name: "", description: "", event_date: "", location: "", link: "", cover_url: "", category: "general" });
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { setErr("Name required"); return; }
    try { await api.post("/events", form); toast.success("Event posted"); onDone(); }
    catch (e2) { setErr(formatApiError(e2)); }
  };
  return (
    <Modal title="// New event" onClose={onClose}>
      <form onSubmit={submit} className="space-y-3">
        <Field label="Event name" required value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="event-name" />
        <Field label="Description" value={form.description} onChange={(v) => setForm({ ...form, description: v })} textarea testid="event-desc" />
        <div className="grid grid-cols-2 gap-3">
          <Field label="Date (any format)" value={form.event_date} onChange={(v) => setForm({ ...form, event_date: v })} placeholder="Feb 15 2026 · 8pm" testid="event-date" />
          <Field label="Location" value={form.location} onChange={(v) => setForm({ ...form, location: v })} placeholder="Jacmel, HT" testid="event-location" />
        </div>
        <Field label="Cover image URL (optional)" value={form.cover_url} onChange={(v) => setForm({ ...form, cover_url: v })} placeholder="https://…" testid="event-cover" />
        <Field label="More info link (optional)" value={form.link} onChange={(v) => setForm({ ...form, link: v })} placeholder="https://…" testid="event-link" />
        <SelectCategory value={form.category} onChange={(v) => setForm({ ...form, category: v })} testid="event-category" />
        {err && <div className="text-xs text-[#ff0033]">{err}</div>}
        <button type="submit" data-testid="submit-event" className="w-full px-6 py-3 bg-[#ff4500] hover:bg-[#ff5722] text-white text-sm">Post event</button>
      </form>
    </Modal>
  );
}

function ProductDialog({ onClose, onDone }) {
  const [form, setForm] = useState({ name: "", description: "", price_display: "", contact_link: "", image_url: "", category: "general" });
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { setErr("Product name required"); return; }
    try { await api.post("/products", form); toast.success("Product listed"); onDone(); }
    catch (e2) { setErr(formatApiError(e2)); }
  };
  return (
    <Modal title="// List product" onClose={onClose}>
      <form onSubmit={submit} className="space-y-3">
        <Field label="Product name" required value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="product-name" />
        <Field label="Description" value={form.description} onChange={(v) => setForm({ ...form, description: v })} textarea testid="product-desc" />
        <Field label="Price / range" value={form.price_display} onChange={(v) => setForm({ ...form, price_display: v })} placeholder="$25 · 2500 HTG" testid="product-price" />
        <Field label="Image URL (optional)" value={form.image_url} onChange={(v) => setForm({ ...form, image_url: v })} placeholder="https://…" testid="product-image" />
        <Field label="Contact link (WhatsApp / IG / phone)" value={form.contact_link} onChange={(v) => setForm({ ...form, contact_link: v })} placeholder="https://wa.me/509…" testid="product-contact" />
        <SelectCategory value={form.category} onChange={(v) => setForm({ ...form, category: v })} testid="product-category" />
        {err && <div className="text-xs text-[#ff0033]">{err}</div>}
        <button type="submit" data-testid="submit-product" className="w-full px-6 py-3 bg-[#ff4500] hover:bg-[#ff5722] text-white text-sm">List product</button>
      </form>
    </Modal>
  );
}

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="w-full max-w-lg bg-[#141414] border border-[#2a2a2a] max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#2a2a2a] sticky top-0 bg-[#141414]">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#a3a3a3] font-mono">{title}</div>
          <button onClick={onClose} data-testid="close-modal"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, required, textarea, placeholder, testid }) {
  const Cmp = textarea ? "textarea" : "input";
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.25em] text-[#a3a3a3]">{label}</label>
      <Cmp required={required} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testid} rows={textarea ? 3 : undefined}
        className="mt-2 w-full bg-[#0a0a0a] border border-[#2a2a2a] focus:border-[#ff4500] outline-none px-3 py-2 text-sm resize-none" />
    </div>
  );
}

function SelectCategory({ value, onChange, testid }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.25em] text-[#a3a3a3]">Category</label>
      <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="mt-2 w-full bg-[#0a0a0a] border border-[#2a2a2a] focus:border-[#ff4500] outline-none px-3 py-2 text-sm appearance-none">
        <option value="general">General</option>
        <option value="carnival">Carnival · Kanaval</option>
        <option value="music">Music · Mizik</option>
        <option value="cuisine">Cuisine · Manje</option>
        <option value="arts">Arts · Atizay</option>
        <option value="history">History · Istwa</option>
        <option value="geography">Geography · Jewografi</option>
      </select>
    </div>
  );
}
